import React, { useState } from "react";
import { signInWithPopup, AuthError } from "firebase/auth";
import { auth, googleProvider } from "../firebase";

const Login: React.FC = () => {
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleGoogleSignIn = async (): Promise<void> => {
    setError("");
    setLoading(true);

    try {
      await signInWithPopup(auth, googleProvider);
      // Redirect is handled by the App component using React Router
    } catch (error) {
      console.error("Authentication error:", error);

      // More specific error handling for domain restrictions
      const authError = error as AuthError;
      if (
        authError.code === "auth/gmail-com-domain-not-allowed" ||
        authError.code === "auth/user-domain-not-allowed"
      ) {
        setError("Only users with a @getplaza.ai email address can sign in.");
      } else {
        setError("Failed to sign in: " + authError.message);
      }
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-xl mb-4 shadow-lg">
            <img
              src="data:image/svg+xml,%3csvg%20width='32'%20height='32'%20viewBox='0%200%20120%20121'%20fill='none'%20xmlns='http://www.w3.org/2000/svg'%3e%3cpath%20fill-rule='evenodd'%20clip-rule='evenodd'%20d='M6.37708%2063.772C8.00311%2090.8351%2029.6647%20112.497%2056.7279%20114.123L6.37708%2063.772ZM64.2515%20114.055C90.5306%20111.998%20111.498%2091.0304%20113.555%2064.7513L64.2515%20114.055ZM113.466%2055.2372C110.99%2029.7693%2090.7306%209.50963%2065.2627%207.03343L113.466%2055.2372ZM55.7367%206.94562C29.4671%209.00811%208.50824%2029.967%206.44576%2056.2366L55.7367%206.94562ZM-2.62268e-06%2060.4999C0%2093.637%2026.8629%20120.5%2060%20120.5C93.1371%20120.5%20120%2093.637%20120%2060.4999C120%2027.3628%2093.1371%200.499872%2060%200.499873C26.8629%200.499875%20-4.07115e-06%2027.3628%20-2.62268e-06%2060.4999ZM109.388%2060.0386L94.5238%2074.9028C88.9785%2067.9436%2081.4226%2062.6517%2072.7336%2059.9086C81.3624%2057.1846%2088.874%2051.947%2094.4085%2045.0592L109.388%2060.0386ZM89.9381%2040.5888L79.2774%2029.9281C71.6444%2036.0879%2066.3854%2045.0599%2065.0704%2055.2664C75.1488%2053.7152%2083.9563%2048.307%2089.9381%2040.5888ZM60.5551%2046.677C63.1982%2038.2893%2068.2159%2030.9562%2074.8159%2025.4666L60.4558%2011.1065L46.1858%2025.3765C52.8394%2030.8758%2057.8977%2038.2434%2060.5551%2046.677ZM56.0825%2055.6084C45.4523%2054.7992%2036.0123%2049.7307%2029.4649%2042.0974L41.7227%2029.8396C49.5007%2036.0701%2054.8361%2045.2113%2056.0825%2055.6084ZM56.1694%2064.2018C55.1182%2074.7959%2049.8295%2084.1442%2042.0136%2090.5286L29.3445%2077.8594C35.9141%2070.127%2045.4385%2064.9945%2056.1694%2064.2018ZM46.4729%2094.9878C52.9848%2089.515%2057.936%2082.2384%2060.5551%2073.9263C63.1598%2082.1925%2068.0709%2089.4346%2074.5296%2094.8969L60.4558%20108.971L46.4729%2094.9878ZM90.051%2079.3756L78.9875%2090.4391C71.3136%2084.1245%2066.0985%2074.9432%2064.9755%2064.5361C75.1573%2066.0778%2084.0489%2071.5547%2090.051%2079.3756ZM25.014%2046.5484L11.5237%2060.0386L24.8921%2073.407C30.3005%2067.1851%2037.3825%2062.4513%2045.4383%2059.9082C37.4428%2057.384%2030.4066%2052.7017%2025.014%2046.5484Z'%20fill='white'/%3e%3c/svg%3e"
              alt="Plaza Logo"
              className="w-8 h-8"
            />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Welcome to Xanathar</h1>
          <p className="text-slate-600">Sign in to your organization account</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center px-6 py-3 border border-slate-300 rounded-lg shadow-sm bg-white text-slate-700 font-medium hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <img
              src="https://developers.google.com/identity/images/g-logo.png"
              alt="Google logo"
              className="w-5 h-5 mr-3"
            />
            {loading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </div>
            ) : (
              "Sign in with Google"
            )}
          </button>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> This application requires a @getplaza.ai email address.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-slate-500">
            Powered by <span className="font-medium text-slate-700">Plaza</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
